import {
  isOpCreateMetadata,
  isOpCreateSample,
  isOpUpdateMetadata,
  isOpUpdateSample
} from '../../../View/Page/HomePage/Modals/CSVModalTypes.js'
import type { OrderRepository } from '../../../Repository/index.ts'
import { concatMap, from, map, mergeMap, of, toArray } from 'rxjs'
import type { OpBatch } from '../../../View/Page/HomePage/Modals/MapToBatches.js'
import type { NewSampleMetaData } from '../../../Model/Sample/ISampleMetaData.js'
import type { NegativeControl, Sample } from '../../../Model/index.js'

export const handleBatchUpdateSamples = (payload: OpBatch, orderRepository: typeof OrderRepository) => {
  const { orderId, poolId, ops } = payload

  const createSamples = ops.filter(isOpCreateSample)
  const createMetadata = ops.filter(isOpCreateMetadata).map((op) => op.metadata)
  const updateMetadata = ops.filter(isOpUpdateMetadata).map((op) => op.metadata)
  const updateSamples = ops.filter(isOpUpdateSample).map((op) => ({ ...op.sample, sampleMetadata: null }))

  return of(null).pipe(
    /* step 1: create any new samples if needed */
    mergeMap(() => {
      return createSamples.length > 0
        ? from(createSamples).pipe(
            concatMap((op) => orderRepository.setContent(op.orderId, op.poolId, op.sample).pipe(map((created) => ({ op, created })))),
            toArray()
          )
        : of([])
    }),
    /* if we had to create any new samples, we have to separately attach metadata to them */
    mergeMap((createdResults: { op: (typeof createSamples)[number]; created: Sample | NegativeControl }[]) => {
      const enrichedMetadata: NewSampleMetaData[] = createdResults.map(({ op, created }) => {
        if (!created?.id) {
          throw new Error(`Created sample missing ID for customerSampleName=${created.customerSampleName}`)
        }

        return {
          ...op.metadata,
          sampleId: created.id
        }
      })

      return of({
        enrichedMetadata,
        createdSamples: createdResults.map(({ created }) => created)
      })
    }),
    mergeMap(({ enrichedMetadata, createdSamples }) =>
      createMetadata.length > 0 || enrichedMetadata.length > 0
        ? orderRepository.addSampleMetaDatas(orderId, [...createMetadata, ...enrichedMetadata]).pipe(
            map((newMetadata) => ({
              newMetadata,
              createdSamples
            }))
          )
        : of({
            newMetadata: [],
            createdSamples
          })
    ),
    mergeMap(({ newMetadata, createdSamples }) =>
      (updateMetadata.length > 0 ? orderRepository.updateSampleMetaDatas(orderId, updateMetadata) : of([])).pipe(
        mergeMap((updatedMetadata) =>
          (updateSamples.length > 0 && poolId ? orderRepository.updatePoolContent(orderId, poolId, updateSamples) : of([])).pipe(
            map((updatedSamples) => ({
              newMetadata,
              updatedMetadata,
              samples: [...updatedSamples, ...createdSamples],
              batch: payload
            }))
          )
        )
      )
    )
  )
}
